import { PinPreview } from '@lessonup/pin-renderer';
import { TeacherPin as BaseTeacherPin } from '@lessonup/pins-shared';
import { Lesson } from '@lessonup/teaching-core';
import { color, rem, styled, TextElement } from '@lessonup/ui-components';
import React from 'react';

type TeacherPin = BaseTeacherPin;

interface ExportLessonPageV2ViewProps {
  teacherPins: TeacherPin[];
  lesson: Lesson;
}

export const ExportLessonPageV2View: React.FC<ExportLessonPageV2ViewProps> = ({ teacherPins }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const showNotes = urlParams.get('show-notes') === 'true';

  return (
    <Container>
      {teacherPins.map((pin) => (
        <PinContainer key={pin.id}>
          <PinPreviewWrapper>
            <PinPreview pin={pin} />
          </PinPreviewWrapper>
          {showNotes && pin.teacherSettings?.notes && (
            <NotesContainer>
              <TextElement textStyle="bodyText" dangerouslySetInnerHTML={{ __html: pin.teacherSettings.notes }} />
            </NotesContainer>
          )}
        </PinContainer>
      ))}
    </Container>
  );
};

const Container = styled.div`
  /* Match V1's A4 dimensions */
  width: 297mm;
  margin: 0 auto;
  padding: 0;
  background: white;
  box-sizing: border-box;
  max-width: 100%;

  @media print {
    width: 100%;
    padding: 0;
    margin: 0;
  }
`;

const PinContainer = styled.div`
  /* Match V1's page break behavior */
  page-break-after: always;
  overflow: hidden;
  margin: 0;
  padding: 0;

  /* Calculate height based on A4 width and 16:9 aspect ratio */
  height: calc(297mm / (16 / 9) - 1px);

  @media print {
    margin: 0;
    padding: 0;
    display: block;
  }
`;

const PinPreviewWrapper = styled.div`
  /* Match V1's slide wrapper */
  width: 297mm;
  height: calc(297mm / (16 / 9) - 1px);
  position: relative;
  border: none;

  /* Ensure pin preview takes full size */
  > div {
    width: 100%;
    height: 100%;
  }
`;

const NotesContainer = styled.div`
  margin-top: ${rem(16)};
  padding: ${rem(28)}; /* Match V1's print margin */
  background-color: ${color.neutral.surface.background};
  border-radius: ${rem(4)};
  width: calc(297mm - ${rem(56)}); /* Full width minus margins on both sides */
  margin-left: auto;
  margin-right: auto;

  @media print {
    break-inside: avoid;
    margin-top: ${rem(24)};
  }
`;
